import type { CategoryNode, SiteNav, SiteNavItem } from '@icetus/contracts';

/**
 * 导航菜单的纯逻辑。
 *
 * 原来这些散在「页头页脚」页里：菜单编辑、菜单摘要、分类拍平、空行清洗。
 * 现在菜单有了自己的一页（侧栏「内容与外观 → 导航菜单」），而「页头页脚」
 * 仍然要读同一份数据做摘要、算预览草稿 —— 两边各抄一份必然会漂：一处改了
 * 清洗规则，另一处保存时又会把空行写进库。
 *
 * 所以「怎么算合法」「怎么显示」放这里，页面只负责摆放。
 */

/**
 * 一个父项最多挂几个子项。与平台侧 `MAX_NAV_CHILDREN` 同一个数：
 * 界面先拦住，别等服务端 400 —— 那种报错只会告诉客户"保存失败"。
 */
export const MAX_NAV_CHILDREN = 8;

/**
 * 位置名的中文。认不出的（主题自定义的）直接显示原名，不猜。
 *
 * 位置由主题声明（`supports.navLocations`），所以这张表不可能穷举；
 * 它只是把常见的四个翻译成人话。
 */
export const LOCATION_LABEL: Record<string, string> = {
  primary: '主导航',
  footer: '页脚菜单',
  legal: '法务链接',
  social: '社交链接',
};

export function locationLabelOf(location: string): string {
  return LOCATION_LABEL[location] ?? location;
}

/**
 * 一项菜单在"能不能存"这一层的判断。
 *
 * 空行丢掉；但**挂了分类的空行不能丢** —— 那种项的名称与地址都由服务端按分类
 * 推出来，控制台里两个输入框都可能是空的，丢掉它等于客户点了「加子项 → 选分类」
 * 之后什么都没发生。
 *
 * 服务端会按"地址不能为空"整单拒掉，客户看到的是一个笼统的 400，会以为整个
 * 保存坏了。空行本来就等于没填，丢掉它才是客户想要的。
 */
export function cleanNavItem(item: SiteNavItem): SiteNavItem | null {
  const label = item.label.trim();
  const href = item.href.trim();
  const categoryId = item.categoryId?.trim() ?? '';
  if (label === '' && categoryId === '') return null;

  const children = (item.children ?? [])
    .map(cleanNavItem)
    .filter((child): child is SiteNavItem => child !== null)
    .slice(0, MAX_NAV_CHILDREN);

  return {
    label,
    href,
    ...(categoryId !== '' ? { categoryId } : {}),
    ...(item.newTab ? { newTab: true } : {}),
    ...(children.length > 0 ? { children } : {}),
  };
}

/**
 * 整份导航的清洗。
 *
 * 清完为空的位置整个删掉，而不是留一个空数组：空数组在库里是一行
 * "客户配过、配了 0 项"的痕迹，日后排查时误导（渲染层判 `length > 0`，
 * 两者行为一样，但"没配过"和"配成空的"该分得出来）。
 */
export function cleanNav(nav: SiteNav): SiteNav {
  const out: SiteNav = {};
  for (const [location, items] of Object.entries(nav)) {
    const kept = items.map(cleanNavItem).filter((item): item is SiteNavItem => item !== null);
    if (kept.length > 0) out[location] = kept;
  }
  return out;
}

/** 分类下拉里显示的名字。没翻译过就退回 slug —— 总比空着让人猜是哪一条好 */
export function categoryLabel(node: CategoryNode): string {
  const first = Object.values(node.translations ?? {})[0];
  return first?.name?.trim() || node.slug;
}

/** 分类是树，下拉里拍平（父在前、子缩进两格），选中任何一个都能用 */
export function flattenCategories(
  nodes: CategoryNode[],
  depth = 0,
): Array<{ node: CategoryNode; depth: number }> {
  const out: Array<{ node: CategoryNode; depth: number }> = [];
  for (const node of nodes) {
    out.push({ node, depth });
    out.push(...flattenCategories(node.children ?? [], depth + 1));
  }
  return out;
}

/**
 * 菜单项当前指向什么，编码成下拉的 value。
 *
 * 三种编码：`cat:<id>`（本站分类）、`page:<path>`（已发布页面）、`custom`
 * （自己填地址）。分类优先 —— 渲染层也是这个优先级。
 */
export function navTargetValue(item: SiteNavItem): string {
  if (item.categoryId) return `cat:${item.categoryId}`;
  if (item.href.trim() === '') return 'custom';
  return `page:${item.href.trim()}`;
}

/**
 * 这一项的「名称」是否真的由客户填过。
 *
 * 挂了分类的项名称可以留空（保存时由服务端按分类名补），所以判"这一项算不算
 * 存在"不能只看 label。
 */
export function navItemHasContent(item: SiteNavItem): boolean {
  return item.label.trim() !== '' || Boolean(item.categoryId);
}

/** 这个位置配了几项（含子项），以及是否已经自定义过 */
export function navStatsOf(nav: SiteNav, location: string): { items: number; customised: boolean } {
  const top = (nav[location] ?? []).filter(navItemHasContent);
  const children = top.reduce(
    (sum, item) => sum + (item.children ?? []).filter(navItemHasContent).length,
    0,
  );
  return { items: top.length + children, customised: top.length > 0 };
}

/**
 * 收起态那一行摘要：几项、分别叫什么。
 *
 * 外壳的区块列表里，导航那一块的内容不在 props 里（存在站点库的 `nav`），
 * 所以不能走通用的 `blockSummary` —— 它会把位置名当成文案，摘要显示一个
 * "primary"，对客户毫无意义。这里直接报菜单里有几项、叫什么。
 */
export function describeNav(nav: SiteNav, location: string): string {
  const items = (nav[location] ?? []).filter(navItemHasContent);
  if (items.length === 0) return '按已发布页面自动生成';
  const names = items
    .map((item) => {
      const label = item.label.trim() || '未命名';
      const kids = (item.children ?? []).filter(navItemHasContent).length;
      /* 「产品（3）」比只写「产品」有用：收起时就能看出这一项是下拉 */
      return kids > 0 ? `${label}（${kids} 个子项）` : label;
    })
    .join(' / ')
    .slice(0, 60);
  return `${items.length} 项菜单 · ${names}`;
}
